/* =====================================================================
 * LiveAgentSettingsModal — 라이브 에이전트 설정 모달 (Voice 선택)
 * ===================================================================== */

import React, { useMemo, useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@auw/workspace-ui';
import { useLiveAgentStore, GEMINI_VOICES } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { useUIStore } from '@auw/stores';

/**
 * Gemini voice style 라벨("Bright", "Easy-going" 등)을 i18n 키로 정규화한다.
 *  - 하이픈 분리 후 첫 토큰만 lower-case, 이후 토큰은 PascalCase → camelCase 결과.
 *  - "Easy-going" → "easyGoing", "Bright" → "bright".
 */
function voiceStyleI18nKey(style: string): string {
  return style
    .split('-')
    .map((part, i) =>
      i === 0
        ? part.toLowerCase()
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join('');
}

// 발화 속도 slider 의 클램프 / 단위 — worklet 의 RATE_MIN/RATE_MAX 보다
// 좁은 사용자 노출 범위. 0.1 단위로 9 단계.
const PLAYBACK_RATE_MIN = 0.8;
const PLAYBACK_RATE_MAX = 1.5;
const PLAYBACK_RATE_STEP = 0.1;

export const LiveAgentSettingsModal: React.FC = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const open = useLiveAgentStore((s) => s.settingsOpen);
  const setOpen = useLiveAgentStore((s) => s.setSettingsOpen);
  const voiceName = useLiveAgentStore((s) => s.voiceName);
  const setVoiceName = useLiveAgentStore((s) => s.setVoiceName);
  const playbackRate = useLiveAgentStore((s) => s.playbackRate);
  const setPlaybackRate = useLiveAgentStore((s) => s.setPlaybackRate);
  const addToast = useUIStore((s) => s.addToast);

  const [pendingVoice, setPendingVoice] = useState(voiceName);
  // 속도 slider 는 모달 열릴 때만 store 값 snapshot, drag 동안 local 상태로
  // 유지하다가 ``적용`` 시 store push. drag 중에 worklet 으로 바로 안 쏘므로
  // 의도치 않은 음질 변동을 사용자가 안 듣게 한다.
  const [pendingRate, setPendingRate] = useState(playbackRate);

  useEffect(() => {
    if (open) {
      setPendingVoice(voiceName);
      setPendingRate(playbackRate);
    }
  }, [open, voiceName, playbackRate]);

  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const pageSidebarOpen = useWorkspaceNavigationStore((s) => s.isPageSidebarOpen('live-agent'));

  const centerPaddingLeft = useMemo(() => {
    let pad = 0;
    if (mainSidebarOpen) pad += parseInt(theme.sizes.sidebarWidth, 10) || 0;
    if (pageSidebarOpen) pad += parseInt(theme.dm.listWidth, 10) || 0;
    return pad;
  }, [mainSidebarOpen, pageSidebarOpen, theme.sizes.sidebarWidth, theme.dm.listWidth]);

  const handleApply = () => {
    if (pendingVoice !== voiceName) {
      setVoiceName(pendingVoice);
      addToast({
        type: 'info',
        title: t('liveAgent.settings.voiceAppliedToast'),
      });
    }
    if (pendingRate !== playbackRate) {
      setPlaybackRate(pendingRate);
    }
    setOpen(false);
  };

  if (!open) return null;

  const hasChange =
    pendingVoice !== voiceName || pendingRate !== playbackRate;

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={t('liveAgent.settings.title')}
      titleIcon={<Settings size={18} />}
      width="640px"
      bodyPadding="0"
      bodyOverflowY="auto"
      centerPaddingLeft={centerPaddingLeft}
    >
      <Section>
        <SectionLabel>{t('liveAgent.settings.voiceLabel')}</SectionLabel>
        <SectionDesc>{t('liveAgent.settings.voiceDesc')}</SectionDesc>
        <VoiceGrid>
          {GEMINI_VOICES.map((v) => {
            const styleLabel = t(
              `liveAgent.voiceStyles.${voiceStyleI18nKey(v.style)}`,
              { defaultValue: v.style },
            );
            return (
              <VoiceCard
                key={v.name}
                $selected={pendingVoice === v.name}
                onClick={() => setPendingVoice(v.name)}
              >
                <VoiceName>{v.name}</VoiceName>
                <VoiceStyle>{styleLabel}</VoiceStyle>
              </VoiceCard>
            );
          })}
        </VoiceGrid>
      </Section>
      <Section>
        <SpeedHeader>
          <div>
            <SectionLabel>
              {t('liveAgent.settings.speedLabel', { defaultValue: '발화 속도' })}
            </SectionLabel>
            <SectionDesc>
              {t('liveAgent.settings.speedDesc', {
                defaultValue: '에이전트가 말하는 속도를 조절하세요',
              })}
            </SectionDesc>
          </div>
          <SpeedValue>{pendingRate.toFixed(1)}x</SpeedValue>
        </SpeedHeader>
        <SpeedRow>
          <SpeedTick>{PLAYBACK_RATE_MIN.toFixed(1)}x</SpeedTick>
          <SpeedSlider
            type="range"
            min={PLAYBACK_RATE_MIN}
            max={PLAYBACK_RATE_MAX}
            step={PLAYBACK_RATE_STEP}
            value={pendingRate}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (!Number.isFinite(next)) return;
              // 0.1 step 부동소수 누적 오차 보정 — 1 자리 반올림.
              setPendingRate(Math.round(next * 10) / 10);
            }}
            aria-label={t('liveAgent.settings.speedLabel', {
              defaultValue: '발화 속도',
            })}
          />
          <SpeedTick>{PLAYBACK_RATE_MAX.toFixed(1)}x</SpeedTick>
        </SpeedRow>
      </Section>
      <Footer>
        <ApplyButton onClick={handleApply} $active={hasChange}>
          {t('liveAgent.settings.apply')}
        </ApplyButton>
      </Footer>
    </Modal>
  );
};

const Section = styled.div`
  padding: 20px 24px 24px;
`;

const SectionLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const SectionDesc = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 16px;
`;

const VoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const VoiceCard = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ theme, $selected }) =>
    $selected
      ? '#E06B50'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme, $selected }) =>
    $selected
      ? theme.mode === 'dark' ? 'rgba(224,107,80,0.12)' : 'rgba(224,107,80,0.06)'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  width: 100%;

  &:hover {
    border-color: ${({ $selected }) => $selected ? '#E06B50' : 'rgba(224,107,80,0.4)'};
    background: ${({ theme, $selected }) =>
      $selected
        ? theme.mode === 'dark' ? 'rgba(224,107,80,0.15)' : 'rgba(224,107,80,0.08)'
        : theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

const VoiceName = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const VoiceStyle = styled.span`
  font-size: 0.75rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SpeedHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const SpeedValue = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #E06B50;
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: right;
`;

const SpeedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SpeedTick = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
  min-width: 28px;
  text-align: center;
`;

const SpeedSlider = styled.input`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  appearance: none;
  -webkit-appearance: none;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #E06B50;
    border: 2px solid ${({ theme }) =>
      theme.mode === 'dark' ? '#2a2a2a' : '#fff'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #E06B50;
    border: 2px solid ${({ theme }) =>
      theme.mode === 'dark' ? '#2a2a2a' : '#fff'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    cursor: pointer;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px 20px;
`;

const ApplyButton = styled.button<{ $active: boolean }>`
  padding: 8px 24px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  background: ${({ $active }) => ($active ? '#E06B50' : 'rgba(128,128,128,0.2)')};
  color: ${({ $active, theme }) =>
    $active ? '#fff' : theme.colors.textMuted};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'auto')};

  &:hover {
    background: ${({ $active }) => ($active ? '#c95a42' : 'rgba(128,128,128,0.3)')};
  }
`;
